import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from 'hooks/useAppDispatch'
import {
  getRestaurantInfo,
  getAnnouncement,
  visit,
} from 'actions/restaurantInfo.action'
import { getCategories, setActiveCategory } from 'actions/categories.action'
import { getSubCategories } from 'actions/subCategories.action'
import { getProducts } from 'actions/products.action'
import getCookie from 'utils/getCookie'
import setCookie from 'utils/setCookie'
import IRestaurantInfo from 'models/IRestaurantInfo'
import IAnnouncement from 'models/IAnnouncement'
import ICategory from 'models/ICategory'
import ISubCategory from 'models/ISubCategory'
import IProduct from 'models/IProduct'
import IVisit from 'models/IVisit'
import { RootState } from 'reducers/rootReducer'

export interface HomeProps {}

export const useHome = (props: HomeProps) => {
  const { restaurantName } = useParams<{ restaurantName: string }>()

  const [showAnnouncementBox, setShowAnnouncementBox] = useState(false)

  const dispatch = useAppDispatch()

  const handleCategoryClick = (id: number) => {
    dispatch(setActiveCategory(id))
  }

  const restaurantInfo: IRestaurantInfo | null = useSelector(
    (state: RootState) => state.restaurantInfo.restaurantInfo
  )
  const announcement: IAnnouncement | null = useSelector(
    (state: RootState) => state.restaurantInfo.announcement
  )
  const activeCategory: number = useSelector(
    (state: RootState) => state.categories.activeCategory
  )
  const categories: ICategory[] | null = useSelector(
    (state: RootState) => state.categories.categories
  )
  const subCategories: ISubCategory[] | null = useSelector(
    (state: RootState) => state.subCategories.subCategories
  )
  const products: IProduct[] | null = useSelector(
    (state: RootState) => state.products.products
  )
  const isLoadingRestaurantInfo: boolean = useSelector(
    (state: RootState) => state.restaurantInfo.isLoading
  )
  const isLoadingCategories: boolean = useSelector(
    (state: RootState) => state.categories.isLoading
  )
  const isLoadingSubCategories: boolean = useSelector(
    (state: RootState) => state.subCategories.isLoading
  )
  const isLoadingProducts: boolean = useSelector(
    (state: RootState) => state.products.isLoading
  )
  const isAnnouncementPublished: boolean =
    restaurantInfo?.isAnnouncementPublished ?? false
  const isLoading =
    isLoadingRestaurantInfo ||
    isLoadingCategories ||
    isLoadingSubCategories ||
    isLoadingProducts

  useEffect(() => {
    try {
      if (!restaurantInfo) dispatch(getRestaurantInfo(restaurantName || ''))
      if (!announcement) dispatch(getAnnouncement(restaurantName || ''))
      if (!categories || !categories.length)
        dispatch(getCategories(restaurantName || ''))
      if (!subCategories || !subCategories.length)
        dispatch(getSubCategories(restaurantName || ''))
      if (!products || !products.length)
        dispatch(getProducts(restaurantName || ''))

      const visited = getCookie('visited')
      const visitedRestaurants = visited ? JSON.parse(visited) : []

      if (!visitedRestaurants.includes(restaurantName)) {
        visitedRestaurants.push(restaurantName)

        setCookie(
          'visited',
          JSON.stringify(visitedRestaurants),
          8 * 60 * 60 * 1000
        )

        const visitBody: IVisit = {
          slug: restaurantName || '',
        }

        dispatch(visit(visitBody))
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (categories && categories.length && activeCategory === 1)
      dispatch(setActiveCategory(categories[0].id))
  }, [categories])

  useEffect(() => {
    if (restaurantInfo) {
      const oldStyleAttributes = document.getElementsByClassName(
        'restaurant-variables'
      )

      if (!oldStyleAttributes.length) {
        const styleTag = document.createElement('style')

        const {
          headerBackground,
          headerTitle,
          headerText,
          headerIcon,
          bodyBackground,
          categoryBackground,
          categoryText,
          categoryActiveText,
          subCategoryTitle,
          productName,
          productPrice,
          productIngredientsBackground,
          productIngredientsText,
          priceListBackground,
          priceListItem,
          nutritionText,
          popularBackground,
          popularText,
          floatingActionsButtonBackground,
          floatingActionsButtonText,
          actionButtonsBackground,
          actionButtonsText,
          popupBackground,
          popupTitle,
          popupButtonBackground,
          popupButtonText,
          popupPlaceholder,
          cartFloatingIconBackground,
          cartFloatingIconText,
          addToCartBackground,
          addToCartText,
          cartScreenBackground,
          cartScreenText,
          cartItemBackground,
          cartButtonBackground,
          cartButtonText,
          cartQuantityBackground,
          cartQuantityText,
          checkoutInputBackground,
          checkoutPlaceholderColor,
          footer,
        } = restaurantInfo.colors

        styleTag.setAttribute('class', 'restaurant-variables')
        styleTag.innerHTML = `
          :root {
            --header-bg: ${headerBackground};
            --header-title: ${headerTitle};
            --header-text: ${headerText};
            --header-icon: ${headerIcon};
            --body-bg: ${bodyBackground};
            --category-bg: ${categoryBackground};
            --category-text: ${categoryText};
            --category-active-text: ${categoryActiveText};
            --sub-category-title: ${subCategoryTitle};
            --product-name: ${productName};
            --product-price: ${productPrice};
            --product-ingredients-bg: ${productIngredientsBackground};
            --product-ingredients-text: ${productIngredientsText};
            --price-list-bg: ${priceListBackground};
            --price-list-item: ${priceListItem};
            --nutrition-text: ${nutritionText};
            --popular-bg: ${popularBackground};
            --popular-text: ${popularText};
            --floating-actions-btn-bg: ${floatingActionsButtonBackground};
            --floating-actions-btn-text: ${floatingActionsButtonText};
            --action-btn-bg: ${actionButtonsBackground};
            --action-btn-text: ${actionButtonsText};
            --popup-bg: ${popupBackground};
            --popup-title: ${popupTitle};
            --popup-btn-bg: ${popupButtonBackground};
            --popup-btn-text: ${popupButtonText};
            --popup-placeholder: ${popupPlaceholder};
            --cart-floating-icon-bg: ${cartFloatingIconBackground};
            --cart-floating-icon-text: ${cartFloatingIconText};
            --add-to-cart-bg: ${addToCartBackground};
            --add-to-cart-text: ${addToCartText};
            --cart-screen-bg: ${cartScreenBackground};
            --cart-screen-text: ${cartScreenText};
            --cart-item-bg: ${cartItemBackground};
            --cart-btn-bg: ${cartButtonBackground};
            --cart-btn-text: ${cartButtonText};
            --cart-quantity-bg: ${cartQuantityBackground};
            --cart-quantity-text: ${cartQuantityText};
            --checkout-input-bg: ${checkoutInputBackground};
            --checkout-placeholder-color: ${checkoutPlaceholderColor};
            --footer-color: ${footer};
          }
        `
        document.head.appendChild(styleTag)

        const { isRTL } = restaurantInfo
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
        document
          .querySelector('.App')
          ?.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
      }

      if (isAnnouncementPublished) {
        const announcementKey = `announcement_shown_${restaurantName}`
        const announcementShown = sessionStorage.getItem(announcementKey)

        if (!announcementShown) {
          setShowAnnouncementBox(true)
          sessionStorage.setItem(announcementKey, 'true')
        }
      }
    }
  }, [restaurantInfo])

  return {
    ...props,
    isRestaurantFound: !!restaurantInfo?.id,
    isLoading,
    activeCategory,
    announcementTitle: announcement?.title,
    announcementDescription: announcement?.description,
    showAnnouncementBox,
    setShowAnnouncementBox,
    handleCategoryClick,
  }
}
